import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { auth as api, type MfaState } from '@/api/endpoints'
import { Alert, Field, Input, Spinner } from '@/components/ui'
import { formatDate } from '@/lib/utils'
import { useAuth } from '@/store/auth'

/**
 * Dvojfaktorové overenie vlastného účtu (`apps.accounts.mfa`).
 *
 * Postup: QR kód do aplikácie → prvý kód → záložné kódy (ukážu sa raz).
 * Kým používateľ prvý kód nepotvrdí, prihlásenie sa nemení - rozbehnuté
 * nastavenie ho nezamkne.
 */
export function MfaSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const user = useAuth((s) => s.user)
  const setUser = useAuth((s) => s.setUser)
  const { data } = useQuery({ queryKey: ['mfa'], queryFn: api.mfa })

  const [setup, setSetup] = useState<MfaState | null>(null)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [codes, setCodes] = useState<string[] | null>(null)
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(
    null,
  )

  const run = useMutation({
    mutationFn: ({
      action,
      payload,
    }: {
      action: 'setup' | 'enable' | 'disable' | 'recovery'
      payload?: { code?: string; password?: string }
    }) => api.mfaAction(action, payload),
    onSuccess: (state, { action }) => {
      setMessage(null)
      qc.setQueryData(['mfa'], { ...state, secret: undefined, uri: undefined, qr: undefined })
      if (user) setUser({ ...user, mfa_enabled: state.enabled })
      if (action === 'setup') {
        setSetup(state)
        setCode('')
      }
      if (action === 'enable') {
        setSetup(null)
        setMessage({ kind: 'success', text: t('mfa.enabled') })
      }
      if (action === 'disable') {
        setPassword('')
        setCodes(null)
        setMessage({ kind: 'success', text: t('mfa.disabled') })
      }
      if (action === 'recovery') setPassword('')
      if (state.recovery_codes) setCodes(state.recovery_codes)
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  if (!data) return null

  return (
    <section className="card space-y-3 p-4">
      <div>
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <ShieldCheck size={16} className={data.enabled ? 'text-emerald-600' : 'text-slate-400'} />
          {t('mfa.title')}
        </h2>
        <p className="text-xs text-slate-500">{t('mfa.intro')}</p>
      </div>

      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      {codes ? (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/60 p-3">
          <p className="text-sm font-semibold">{t('mfa.codesTitle')}</p>
          <p className="text-xs text-slate-600">{t('mfa.codesHint')}</p>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm sm:grid-cols-5">
            {codes.map((c) => (
              <li key={c} className="rounded bg-white px-2 py-1 text-center">
                {c}
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}
            >
              {t('mfa.copy')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCodes(null)}>
              {t('mfa.saved')}
            </button>
          </div>
        </div>
      ) : null}

      {data.enabled ? (
        <div className="space-y-3">
          <p className="text-sm">
            <span className="font-medium text-emerald-700">{t('mfa.on')}</span>
            {data.enabled_at ? ` · ${t('mfa.since', { date: formatDate(data.enabled_at) })}` : ''}
            {' · '}
            {t('mfa.codesLeft', { count: data.recovery_left })}
          </p>
          <Field label={t('mfa.passwordToChange')} className="sm:max-w-xs">
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary"
              disabled={!password || run.isPending}
              onClick={() => run.mutate({ action: 'recovery', payload: { password } })}
            >
              {t('mfa.newCodes')}
            </button>
            <button
              type="button"
              className="btn-secondary text-red-700"
              disabled={!password || run.isPending}
              onClick={() => run.mutate({ action: 'disable', payload: { password } })}
            >
              {t('mfa.turnOff')}
            </button>
          </div>
        </div>
      ) : setup ? (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            run.mutate({ action: 'enable', payload: { code } })
          }}
        >
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>{t('mfa.step1')}</li>
            <li>{t('mfa.step2')}</li>
          </ol>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {setup.qr ? (
              <img src={setup.qr} alt="" className="h-44 w-44 rounded border border-slate-200 bg-white" />
            ) : null}
            <div className="space-y-1 text-xs text-slate-500">
              <p>{t('mfa.manual')}</p>
              <code className="block break-all rounded bg-slate-100 px-2 py-1 text-slate-800">
                {setup.secret}
              </code>
            </div>
          </div>
          <Field label={t('mfa.code')} className="sm:max-w-xs">
            <Input
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123 456"
            />
          </Field>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={code.trim().length < 6 || run.isPending}>
              {run.isPending ? <Spinner /> : null}
              {t('mfa.confirm')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setSetup(null)}>
              {t('global.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          className="btn-primary"
          disabled={run.isPending}
          onClick={() => run.mutate({ action: 'setup' })}
        >
          {run.isPending ? <Spinner /> : null}
          {t('mfa.turnOn')}
        </button>
      )}
    </section>
  )
}
